import { useCallback } from 'react';

import * as E from 'fp-ts/Either';
import { pipe } from 'fp-ts/function';
import * as T from 'fp-ts/Task';
import * as TE from 'fp-ts/TaskEither';
import type { WritableAtom } from 'jotai';
import { useAtom } from 'jotai';

import { BSTaskFromIO } from '../../util/bs-fp';
import type { BSError } from '../error/BSError';
import type { BSTask } from '../types';

export interface AsyncAtomData<T> {
  data: T | null;
  error: BSError | null;
  received: boolean;
  updating: boolean;
}

export const useAsyncAtomState = <T>(
  atom: WritableAtom<AsyncAtomData<T>, [AsyncAtomData<T> | ((prev: AsyncAtomData<T>) => AsyncAtomData<T>)], void>,
) => {
  const [state, setState] = useAtom<AsyncAtomData<T>>(atom);

  const execute = useCallback(
    async (
      asyncFn: () => BSTask<T>,
      onError?: (error: BSError) => void | Promise<void>,
      onSuccess?: (data: T) => void | Promise<void>,
    ) => {
      const task = pipe(
        BSTaskFromIO(() => setState((prev) => ({ ...prev, updating: true }))),
        TE.chain(() => asyncFn()),
        T.map(
          E.fold(
            async (error) => {
              await onError?.(error);
              setState({
                data: null,
                error,
                received: true,
                updating: false,
              });
            },
            async (data) => {
              await onSuccess?.(data);
              setState({
                data,
                error: null,
                received: true,
                updating: false,
              });
            },
          ),
        ),
      );

      await task();
    },
    [setState],
  );

  return { ...state, execute };
};
